import React, { FC, useState, useEffect, useCallback, memo } from 'react';
import useDidUpdate from '../../../../hooks/useDidUpdate.effect';
import {
  TCountryDropdownContextSendData,
  TCountryStateDropDownField,
  TCountryStateField,
  TDropdownContextSendData,
} from '../../types/countryStateField.types';
import CountryStateDropdowns from './components/countryStateDropdowns';
import { APIPrefix } from '../../../../constants/api.constants';
import useDidCountryUpdate from '../../hooks/useDidCountryUpdate';
import './styles.scss';
import useSitecoreContext from '../../../../hooks/useSitecoreContext';

const convertObjectToString = (obj) => {
  const objKeys = Object.keys(obj);
  if (objKeys.length === 0) {
    return '';
  }
  return objKeys?.map((key) => obj[key]).join('|');
};

const defaultSendData = {
  value: [
    {
      country: '',
      state: '',
    },
  ],
};

const CountryStateField: FC<TCountryStateDropDownField> = (props) => {
  const { field } = props;
  const id = field?.valueField?.id;
  const name = field?.valueField?.name;
  const FieldValidationErrors = props?.fieldValidationErrorsComponent;
  const [fetchedData, setFetchedData] = useState<TCountryStateField[]>([]);
  const [defaultCountry, setDefaultCountry] = useState('');
  const [useStateCode, setUseStateCode] = useState(false);
  const {sitecoreContext} = useSitecoreContext();
  const itemID = sitecoreContext?.route?.itemId;
  const siteLanguage = sitecoreContext?.language;
  const [value, setValue] = useState('');
  const setCountry= useDidCountryUpdate();

  const fetchData = useCallback(async () => {
    const getApiURL = `/${APIPrefix}/countrystate/list/${itemID}/${siteLanguage}`;
    try {
      const res = await fetch(getApiURL);
      if (!res.ok) {
        throw Error(`${res.status} : Failed to fetch data`);
      }
      const result = await res.json();
      setFetchedData(result?.countries);
      setDefaultCountry(result?.defaultCountry);
      setUseStateCode(result?.useStateCode);
    } catch (error) {
      console.error(`${error} occurred while fetching data`);
    }
  }, []);

  const [sendData, setSendData] = useState<TCountryDropdownContextSendData>(() => defaultSendData);

  useEffect(() => {
    itemID && fetchData();
  }, [fetchData]);

  const updateSendData = (newSendData: Partial<TDropdownContextSendData> = {}) => {
    setSendData((prevState) => ({
      ...prevState,
      value: prevState.value.map((item) => ({ ...item, ...newSendData })),
    }));
  };

  let sendCountryStateValue = sendData?.value?.map((obj) => convertObjectToString(obj)).join(',');
  const isEmptyState = fetchedData?.find((item) => item?.country === sendData?.value?.[0]?.country)?.states?.length === 0;

  if (sendCountryStateValue.endsWith('|') && (isEmptyState || field?.model?.hideStateProvinceDropdown)) {
    sendCountryStateValue = sendCountryStateValue.split('|')?.[0].trim();
  }

  const getSelectedCountry = (country) => {
    const matchCountry = fetchedData?.find((item) => item?.country === country);
    setCountry(matchCountry?.countryisocode)
  };

  useDidUpdate(() => {
    const hasEmptyCountryValues = sendData.value.some((obj) => obj.country?.trim() === '');
    const sendValue = hasEmptyCountryValues ? '' : sendCountryStateValue;

    setValue(sendValue);
    props.onChange(field.valueField.name, sendValue, props.isValid, []);
    getSelectedCountry(sendCountryStateValue.split('|')?.[0].trim());
  }, [sendData]);

  if (!fetchedData?.length) {
    return null;
  }

  return (
    <div className="sitecore-jss-form__control--countryState-dropdown-wrapper">
      <input id={id} name={name} className="sitecore-jss-form__field" type="hidden" value={value} form="novalidatedform" />

      <div className="sitecore-jss-form__control--countryState-dropdown-list">
        <CountryStateDropdowns
          fields={props}
          defaultCountry={defaultCountry}
          optionsList={fetchedData}
          updateSendData={updateSendData}
          useStateCode={useStateCode}
          hideStateProvinceDropdown={field?.model?.hideStateProvinceDropdown}
        />
      </div>

      {FieldValidationErrors && <FieldValidationErrors {...props} />}
    </div>
  );
};

export default memo(CountryStateField);
